import * as React from 'react'
import { useIntl } from 'react-intl'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import { Chip, TableHead } from '@mui/material'
import ConfirmDelete from '../../../../components/dialogs/ConfirmDelete'
import ResetTemporaryPassword from '../../../../components/dialogs/ResetTemporaryPassword'
import { capitalize } from '../../../../utils/functions'
import { SubjectState, TeacherState } from '../../../../features/types'
import AddTeacherDialog from './AddTeacherDialog'
import { tone } from '../../../../utils/uiTokens'

interface Props {
    teachersData: TeacherState[]
    isFetching: boolean
    onResetPassword: (id: number) => void
    onDelete: (subject: number) => void
    onToggleBlock: (row: TeacherState) => void
}

const TeachersTable: React.FC<Props> = ({
    teachersData = [],
    isFetching,
    onDelete,
    onResetPassword,
    onToggleBlock,
}) => {
    const { formatMessage, locale } = useIntl()

    return (
        <Box>
            <TableContainer
                component={Paper}
                sx={{ border: 1, borderColor: 'grey.100', borderRadius: 2 }}
                elevation={0}
            >
                <Table
                    sx={{ minWidth: 500 }}
                    aria-label="custom pagination table"
                >
                    <TableHead sx={{ bgcolor: 'grey.100' }}>
                        <TableRow>
                            <TableCell>
                                {formatMessage({ id: 'fullName' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'phone' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'temporaryPassword' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'subjects' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'role' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'staffStatus' })}
                            </TableCell>

                            <TableCell align="right">
                                {formatMessage({ id: 'edit' })}
                            </TableCell>

                            <TableCell align="right">
                                {formatMessage({ id: 'delete' })}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    {teachersData?.length ? (
                        <TableBody>
                            {/* {(rowsPerPage > 0
                            ? teachersData.slice(
                                  page * rowsPerPage,
                                  page * rowsPerPage + rowsPerPage,
                              )
                            : teachersData
                            )*/}
                            {teachersData?.map((row) => (
                                <TableRow key={row?.id}>
                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {row?.lastName} {row?.firstName}
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {row?.mobile}
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        <ResetTemporaryPassword
                                            password={row?.password}
                                            fullName={
                                                row?.lastName +
                                                ' ' +
                                                row?.firstName
                                            }
                                            isLoading={isFetching}
                                            confirmAction={() =>
                                                onResetPassword(
                                                    row?.id as number,
                                                )
                                            }
                                        />
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {row?.subjects
                                            ?.map((subject: SubjectState) => {
                                                return subject?.[
                                                    `title${capitalize(locale)}`
                                                ]
                                            })
                                            ?.join(', ')}
                                    </TableCell>

                                    <TableCell>
                                        {formatMessage({
                                            id: row?.role?.toLowerCase(),
                                        })}
                                    </TableCell>

                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={formatMessage({ id: row?.blocked ? 'staffBlocked' : 'staffActive' })}
                                            onClick={() => onToggleBlock(row)}
                                            sx={{
                                                fontWeight: 700,
                                                backgroundColor: (row?.blocked ? tone.bad : tone.ok).bg,
                                                color: (row?.blocked ? tone.bad : tone.ok).text,
                                                border: `1px solid ${(row?.blocked ? tone.bad : tone.ok).border}`,
                                            }}
                                        />
                                    </TableCell>

                                    <TableCell
                                        style={{ width: 60 }}
                                        align="right"
                                    >
                                        <AddTeacherDialog
                                            editMode={true}
                                            editTeacher={row}
                                        />
                                    </TableCell>

                                    <TableCell
                                        style={{ width: 60 }}
                                        align="right"
                                    >
                                        <ConfirmDelete
                                            confirmAction={() =>
                                                onDelete(row?.id as number)
                                            }
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    ) : (
                        !isFetching && (
                            <TableBody>
                                <TableRow>
                                    <TableCell
                                        component="th"
                                        scope="row"
                                        align="center"
                                        colSpan={8}
                                    >
                                        {formatMessage({ id: 'noData' })}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        )
                    )}

                </Table>
            </TableContainer>
        </Box>
    )
}

export default TeachersTable
